import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { pagesAPI } from '../utils/api';
import { ArrowLeft, Clock, User, MapPin, AlertTriangle, CheckCircle, ArrowUp, ArrowDown, FileText } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import TaskChat from '../components/TaskChat';

const PageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responseMessage, setResponseMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPage();
  }, [id]);

  const fetchPage = async () => {
    try {
      const response = await pagesAPI.getById(id);
      setPage(response.data);
    } catch (error) {
      console.error('Error fetching page:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickResponse = async (type, message, eta = null) => {
    setSubmitting(true);
    try {
      await pagesAPI.respond(id, { type, message, eta });
      await fetchPage();
    } catch (error) {
      console.error('Error responding:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePriorityChange = async (newPriority) => {
    if (window.confirm(`Change priority to ${newPriority}?`)) {
      setSubmitting(true);
      try {
        await pagesAPI.update(id, { priority: newPriority });
        await fetchPage();
      } catch (error) {
        console.error('Error updating priority:', error);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleReview = async () => {
    setSubmitting(true);
    try {
      await pagesAPI.review(id);
      await fetchPage();
    } catch (error) {
      console.error('Error marking as reviewed:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async () => {
    const diagnosis = prompt('Enter diagnosis/outcome:');
    if (!diagnosis) return;
    
    const actionTaken = prompt('Enter action taken:');
    if (!actionTaken) return;

    setSubmitting(true);
    try {
      await pagesAPI.complete(id, {
        diagnosis,
        actionTaken,
        followUpNeeded: false,
        timeToComplete: Math.round((new Date() - new Date(page.createdAt)) / 1000 / 60)
      });
      navigate(user.role === 'doctor' ? '/doctor' : '/nurse');
    } catch (error) {
      console.error('Error completing page:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading page details...</p>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Page not found</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  const getPriorityColor = (priority) => {
    const colors = {
      1: 'bg-red-100 text-red-800 border-red-300',
      2: 'bg-blue-100 text-blue-800 border-blue-300',
      3: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      4: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colors[priority] || colors[4];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2 gap-3">
            <div className="flex items-center gap-2 sm:gap-4">
              <img src="/REACH_LOGO.png" alt="Reach Logo" className="h-10 sm:h-12 w-auto" />
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1 sm:gap-2 text-gray-600 hover:text-gray-900 text-sm sm:text-base"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                Back
              </button>
            </div>
            <button
              onClick={() => navigate(`/patient-history/${id}`)}
              className="flex items-center gap-1 sm:gap-2 bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base w-full sm:w-auto justify-center"
            >
              <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">View Patient History</span>
              <span className="sm:hidden">Patient History</span>
            </button>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Task Details</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
        {/* Priority Badge */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`px-4 py-2 rounded-lg text-sm font-medium border ${getPriorityColor(page.priority)}`}>
            Priority {page.priority}
          </span>
          <span className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-800 capitalize">
            {page.status.replace('_', ' ')}
          </span>
          {page.reviewed && (
            <span className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-green-100 text-green-800 border border-green-300">
              <CheckCircle className="w-4 h-4" />
              Clinically Reviewed
            </span>
          )}
        </div>

        {/* Escalation Alert */}
        {page.escalationReason && (
          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900 mb-1">⚠️ AUTO-ESCALATED BY EHR DATA</h3>
                <p className="text-red-800">{page.escalationReason}</p>
              </div>
            </div>
          </div>
        )}

        {/* Patient Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Patient Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">URN</p>
                <p className="font-medium text-lg">{page.patient.nhsNumber}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Location</p>
                <p className="font-medium">{page.patient.location}</p>
              </div>
            </div>
            {page.patient.age && (
              <div>
                <p className="text-sm text-gray-600">Age</p>
                <p className="font-medium">{page.patient.age} years</p>
              </div>
            )}
          </div>
        </div>

        {/* EHR Flags */}
        {page.ehrFlags && page.ehrFlags.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-orange-900 mb-4">⚠️ EHR Alerts</h2>
            <div className="space-y-3">
              {page.ehrFlags.map((flag, index) => (
                <div key={index} className="bg-white rounded-lg p-4">
                  <p className="font-medium text-gray-900">{flag.message}</p>
                  {flag.recommendation && (
                    <p className="text-sm text-gray-600 mt-1">→ {flag.recommendation}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Clinical Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Clinical Details</h2>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">Request Type</p>
              <p className="font-medium capitalize">{page.requestType.replace('_', ' ')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Clinical Urgency</p>
              <p className="font-medium capitalize">{page.clinicalUrgency.replace('_', ' ')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Reason for Page</p>
              <p className="text-gray-900 whitespace-pre-wrap">{page.reason}</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="w-4 h-4" />
              <span>Created {page.createdAt ? formatDistanceToNow(new Date(page.createdAt), { addSuffix: true }) : 'just now'}</span>
            </div>
          </div>
        </div>

        {/* AI Priority Reasoning */}
        {page.priorityReasoning && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-blue-900 mb-4">🤖 AI Priority Analysis</h2>
            <div className="text-sm text-blue-800 whitespace-pre-wrap leading-relaxed">
              {page.priorityReasoning}
            </div>
            {page.priorityConfidence && (
              <p className="text-sm text-blue-700 mt-4 font-medium">
                Confidence: {page.priorityConfidence}%
              </p>
            )}
          </div>
        )}

        {/* Responses */}
        {page.responses && page.responses.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Responses</h2>
            <div className="space-y-3">
              {page.responses.map((response, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium">{response.userName}</span>
                    <span className="text-sm text-gray-600">
                      {response.timestamp ? formatDistanceToNow(new Date(response.timestamp), { addSuffix: true }) : 'just now'}
                    </span>
                  </div>
                  <p className="text-gray-700">{response.message}</p>
                  {response.eta && (
                    <p className="text-sm text-blue-600 mt-1">ETA: {response.eta}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Doctor Actions */}
        {user.role === 'doctor' && page.status !== 'completed' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            
            {/* Priority Adjustment */}
            <div className="mb-4 pb-4 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Adjust Priority</h3>
              <div className="flex flex-wrap gap-2">
                {page.priority > 1 && (
                  <button
                    onClick={() => handlePriorityChange(page.priority - 1)}
                    disabled={submitting}
                    className="flex items-center gap-1 px-2 sm:px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-xs sm:text-sm flex-1 sm:flex-initial justify-center"
                  >
                    <ArrowUp className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Escalate to P{page.priority - 1}</span>
                    <span className="sm:hidden">↑ P{page.priority - 1}</span>
                  </button>
                )}
                {page.priority < 4 && (
                  <button
                    onClick={() => handlePriorityChange(page.priority + 1)}
                    disabled={submitting}
                    className="flex items-center gap-1 px-2 sm:px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 text-xs sm:text-sm flex-1 sm:flex-initial justify-center"
                  >
                    <ArrowDown className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Downgrade to P{page.priority + 1}</span>
                    <span className="sm:hidden">↓ P{page.priority + 1}</span>
                  </button>
                )}
                <span className="flex items-center text-xs sm:text-sm text-gray-600 w-full sm:w-auto sm:ml-2 mt-2 sm:mt-0">
                  Current: Priority {page.priority}
                </span>
              </div>
            </div>

            {/* Response Actions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => handleQuickResponse('on_my_way', 'On my way - ETA 5 mins', '5 mins')}
                disabled={submitting}
                className="px-3 sm:px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm sm:text-base"
              >
                On My Way (5 min)
              </button>
              <button
                onClick={() => handleQuickResponse('on_my_way', 'On my way - ETA 15 mins', '15 mins')}
                disabled={submitting}
                className="px-3 sm:px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm sm:text-base"
              >
                On My Way (15 min)
              </button>
              <button
                onClick={() => handleQuickResponse('need_info', 'Need more information')}
                disabled={submitting}
                className="px-3 sm:px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 text-sm sm:text-base"
              >
                Need More Info
              </button>
              {!page.reviewed ? (
                <button
                  onClick={handleReview}
                  disabled={submitting}
                  className="px-3 sm:px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <CheckCircle className="w-4 h-4" />
                  Mark Reviewed
                </button>
              ) : (
                <div className="px-3 sm:px-4 py-3 bg-green-100 text-green-800 rounded-lg flex items-center justify-center gap-2 font-medium text-sm sm:text-base">
                  <CheckCircle className="w-4 h-4" />
                  Reviewed
                </div>
              )}
              <button
                onClick={handleComplete}
                disabled={submitting}
                className="px-3 sm:px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 col-span-1 sm:col-span-2 text-sm sm:text-base"
              >
                Mark Complete
              </button>
            </div>
          </div>
        )}
      </main>
      
      {/* Task Chat - Fixed position bottom right */}
      <TaskChat pageId={id} currentUser={user} />
    </div>
  );
};

export default PageDetail;
